import { FC, Key, useMemo, useState, CSSProperties } from 'react';
import { ProDatePicker } from '../antd';
import FormItem from './subpage/formItem';
import SearchFormItem from './subpage/searchFormItem';
import SearchFormDisabled from './subpage/disabled';
import dayjs, { Dayjs } from 'dayjs';
import {
  BaseSearchFormItemProps,
  SearchFormItemEnum,
} from './interface/baseInterface';
import { DatePickerProps } from 'antd/lib/date-picker';
import { DEFAULT_DATA_FORMAT } from '../constants/constant';
import { Rule } from 'antd/lib/form';
import { NodeKey } from '../constants/interface';

export interface SearchFormTimeSlotProps extends BaseSearchFormItemProps {
  /** 日期属性配置 */
  datePickerProps?: DatePickerProps;
  /** 日期之间分隔符  */
  middleText?: string;
  /** 分隔符宽度 */
  middleWidth?: number;
  /** 是否必传 */
  allRequired?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 提示文字 */
  placeholder?: string[] | string;
  /** 开始时间验证 */
  startRules?: Rule[];
  /** 结束时间验证 */
  endRules?: Rule[];
  /** 初始默认值 */
  initialValue?: string[];
}
/** 日期时间组合 */
const SearchFormTimeSlot: FC<SearchFormTimeSlotProps> = props => {
  const {
    datePickerProps,
    render,
    disabledRender,
    isDetail,
    form,
    isTable,
    name,
    testId,
    label,
    hideLabel,
    startRules,
    endRules,
    middleText,
    middleWidth,
    placeholder,
    allRequired,
    initialValue,
    ...resProps
  } = props;
  const [startMoment, setStartMoment] = useState<Dayjs | null>();
  const [endMoment, setEndMoment] = useState<Dayjs | null>();
  let startName: NodeKey = 'startTime';
  let endName: NodeKey = 'endTime';
  if (Array.isArray(name)) {
    startName = name[0];
    endName = name[1];
  }
  const formItemStyle: CSSProperties = useMemo(() => {
    const difWidth = Math.floor((middleWidth || 32) / 2);
    return {
      display: 'inline-block',
      width: `calc(50% - ${difWidth}px)`,
      marginBottom: 0,
      marginRight: 0,
    };
  }, [middleWidth]);
  const classes = useMemo(() => {
    if (allRequired) return 'address-required';
    if (Array.isArray(startRules) && Array.isArray(endRules)) {
      const startFind = startRules.find((v: any) => v.required);
      const endFind = endRules.find((v: any) => v.required);
      if (startFind && endFind) return 'address-required';
    }
  }, [allRequired, startRules, endRules]);
  const setRules = (
    type: 'start' | 'end',
    required?: boolean,
    itemRules: Rule[] = [],
  ) => {
    if (required) {
      const findItem = itemRules?.find((v: any) => v.required);
      if (findItem) return itemRules;
      return [
        {
          required: true,
          message: `请选择${type === 'start' ? '开始' : '结束'}时间`,
        },
        ...itemRules,
      ];
    }
    return itemRules;
  };
  const setInitialValue = (val?: any) => {
    if (dayjs.isDayjs(val)) return val;
    if (typeof val === 'string') return dayjs(val);
  };
  // 开始时间改变
  const handleStartChange = (date: Dayjs | null) => {
    setStartMoment(date);
  };
  // 结束时间改变
  const handleEndChange = (date: Dayjs | null) => {
    setEndMoment(date);
  };
  return (
    <SearchFormItem
      {...resProps}
      label={!hideLabel && label}
      className={classes}
    >
      {isDetail || (datePickerProps && datePickerProps.disabled) ? (
        <SearchFormDisabled
          form={form}
          names={[{ name: startName }, { name: endName }]}
          isMoment
          render={render || disabledRender}
          // @ts-ignore
          format={datePickerProps?.format || DEFAULT_DATA_FORMAT}
          isTable={isTable}
          splitSymbol={middleText}
        />
      ) : (
        <>
          <FormItem
            label={Array.isArray(placeholder) ? placeholder[0] : placeholder}
            hideLabel={true}
            {...resProps}
            initialValue={setInitialValue(initialValue?.[0])}
            style={formItemStyle}
            name={startName}
            form={form}
            rules={setRules('start', allRequired, startRules)}
          >
            <ProDatePicker
              style={{ width: '100%' }}
              format={DEFAULT_DATA_FORMAT}
              // @ts-ignore
              disabledDate={(date: Moment) => {
                if (endMoment) return date > endMoment;
                return false;
              }}
              // @ts-ignore
              onChange={handleStartChange}
              {...datePickerProps}
              placeholder={
                Array.isArray(placeholder) ? placeholder[0] : placeholder
              }
            />
          </FormItem>
          <div
            style={{
              display: 'inline-block',
              width: 32,
              height: 28,
              lineHeight: '28px',
              textAlign: 'center',
            }}
          >
            {middleText}
          </div>
          <FormItem
            label={Array.isArray(placeholder) ? placeholder[1] : placeholder}
            hideLabel={true}
            {...resProps}
            initialValue={setInitialValue(initialValue?.[1])}
            style={formItemStyle}
            name={endName}
            form={form}
            rules={setRules('end', allRequired, endRules)}
          >
            <ProDatePicker
              style={{ width: '100%' }}
              format={DEFAULT_DATA_FORMAT}
              // @ts-ignore
              onChange={handleEndChange}
              // @ts-ignore
              disabledDate={(date: Moment) => {
                if (startMoment) return date < startMoment;
                return false;
              }}
              {...datePickerProps}
              placeholder={
                Array.isArray(placeholder) ? placeholder[1] : placeholder
              }
            />
          </FormItem>
        </>
      )}
    </SearchFormItem>
  );
};

SearchFormTimeSlot.defaultProps = {
  middleText: '-',
  placeholder: ['开始时间', '结束时间'],
};

SearchFormTimeSlot.displayName = SearchFormItemEnum.SearchFormTimeSlot;

export default SearchFormTimeSlot;
